import { defineStore, storeToRefs } from "pinia"
import { computed, ref, watch } from "vue"
import { useRoute } from 'vue-router'
import { usePageVisibilityStore } from "@/stores/pageVisibility"
import { usePageFocusObserver } from "@/stores/pageFocusObserver";
import { axiosRetryx } from '@/utils/axios_retryx'

export const useVersionChecksStore = defineStore("versionChecks", () => {
  // 現在のリリースバージョン
  const kuraemonCloudWebPageVersion = ref('')

  // 直近の更新実行時のリリースバージョン(※古くなっていることもある)
  const storedKuraemonCloudWebPageVersion = ref('')

  const webPageRefreshRequired = computed(() => (
    kuraemonCloudWebPageVersion.value !== storedKuraemonCloudWebPageVersion.value
  ))

  const checkWebPageVersion = async () => {
    const curVersion = await getKcloudWebPageVersionFromJson()
    if (curVersion == undefined) return

    const storedVersion = storedKuraemonCloudWebPageVersion.value
    storedKuraemonCloudWebPageVersion.value = curVersion

    // 初回は最新を読み込んだ直後なのでリロード不要（WebViewはストレージが毎回空のため常にここを通る）
    if (!storedVersion) return

    if (String(storedVersion) !== String(curVersion)) {
      window.location.reload();
    }
  }

  // jsonからWEBバージョン取得
  const getKcloudWebPageVersionFromJson = async (): Promise<undefined | string> => {
    const kcloudWebPageVersion = await axiosRetryx({
      method: 'get',
      url: "./kcloud_web_page_version.json",
      headers: { 'Cache-Control': 'no-cache' },
    })

    if (!kcloudWebPageVersion) return
    if (!("data" in kcloudWebPageVersion)) return
    if (!("version" in kcloudWebPageVersion.data)) return 

    kuraemonCloudWebPageVersion.value = kcloudWebPageVersion.data.version as string
    return kuraemonCloudWebPageVersion.value
  }

  // 画面が非表示→表示に切り替わったときに、バージョンjson取得
  const { pageVisible } = storeToRefs(usePageVisibilityStore())
  watch(
    () => pageVisible.value,
    (newPageVisible) => {
      if (newPageVisible) {
        getKcloudWebPageVersionFromJson()
      }
    }
  )

  // window focusで、バージョンjson取得
  const { subscribe } = usePageFocusObserver();
  subscribe({
    onPageFocus() {
      getKcloudWebPageVersionFromJson()
    },
  });

  // ルートに切り替わったときに、バージョンjson取得(※ログイン画面と工事一覧はチェックするので除外する)
  const omitRouteNames = ["Login", "ConstructionList"]
  const route = useRoute()
  watch(
    () => route.name,
    (newRouteName) => {
      if (!newRouteName) return
      if (omitRouteNames.includes(newRouteName.toString())) return
      getKcloudWebPageVersionFromJson()
    }
  )

  return {
    storedKuraemonCloudWebPageVersion,
    kuraemonCloudWebPageVersion,
    webPageRefreshRequired,
    checkWebPageVersion,
  };
},
{
  persist: {
    key: 'KuraemonCloud2_stores.versionChecks',
    storage: localStorage,
    paths: ['storedKuraemonCloudWebPageVersion'],
  }
},
);